import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import { Alert, Box, Chip, DialogContent, Divider, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import useViewportMode from '../../hooks/useViewportMode';
import { RoleTemplateReplay } from '../../services/RoleTemplateReplayService';
import ButtonEx from '../ButtonEx';
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from '../responsive';
import SyntheticTemplateReplayPresentation from './SyntheticTemplateReplayPresentation';

interface RoleTemplateReplayPresentationProps {
    replay: RoleTemplateReplay;
    onClose?: () => void;
}

const DetailRow = ({ icon, label, value, secondary }: {
    icon: React.ReactNode;
    label: string;
    value: string;
    secondary?: string;
}) => <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box color="primary.main" sx={{ display: 'flex', pt: 0.25 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography fontWeight={700}>{value}</Typography>
        {secondary && <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{secondary}</Typography>}
    </Box>
</Stack>;

const RoleTemplateReplayPresentation = ({ replay, onClose = () => undefined }: RoleTemplateReplayPresentationProps) => {
    const viewport = useViewportMode();
    const [view, setView] = useState<'role' | 'template'>('role');
    const compact = viewport.isPhone || (viewport.isTablet && viewport.isPortrait);

    if (view === 'template')
        return <SyntheticTemplateReplayPresentation
            replay={replay.Template}
            onBack={() => setView('role')}
            onClose={onClose}
            showDecisionReplay
        />;

    return <ResponsiveDialog
        open
        fullWidth
        widthTier="comfortable"
        sizingStrategy="workspace"
        desktopViewportGutter={32}
        aria-labelledby="role-template-replay-title"
        onClose={onClose}
        PaperProps={{ sx: { maxHeight: 'calc(100% - 64px)', overflow: 'hidden' } }}>
        <ResponsiveDialogHeader
            id="role-template-replay-title"
            title="Field Service Technician"
            subtitle="Role foundation · Step 1 of 2"
            actions={!viewport.isPhone && <Stack direction="row" spacing={1}>
                <Chip label="SYNTHETIC" color="info" variant="outlined" />
                <Chip label="READ ONLY" color="info" variant="outlined" />
            </Stack>}
            onClose={onClose}
            viewportOverride={viewport.mode}
        />
        <DialogContent
            dividers
            data-testid="role-template-replay-content"
            sx={{ minHeight: 0, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
            <Stack spacing={2.5}>
                <Alert
                    severity="info"
                    variant="filled"
                    data-testid="role-template-replay-banner"
                    sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5}>
                        <Typography fontWeight={800}>SYNTHETIC DEMONSTRATION · READ ONLY</Typography>
                        <Typography>{replay.OrganizationName}</Typography>
                    </Stack>
                </Alert>

                <Box
                    data-testid="role-template-replay-layout"
                    data-layout={compact ? 'compact' : viewport.isTablet ? 'tablet-landscape' : 'desktop'}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: compact ? '1fr' : 'minmax(0, 1.45fr) minmax(300px, 0.55fr)',
                        gap: 2.5,
                        alignItems: 'start',
                    }}>
                    <Paper variant="outlined" component="section" aria-labelledby="role-activities-title" sx={{ p: { xs: 2, md: 2.5 } }}>
                        <Stack spacing={2}>
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <WorkOutlineRoundedIcon color="primary" />
                                    <Typography id="role-activities-title" variant="h6">Approved role activities</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">The position defines the work before the review begins.</Typography>
                            </Box>
                            <Box
                                data-testid="role-template-activity-grid"
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: viewport.isDesktop ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                                    gap: 1.5,
                                }}>
                                {replay.Activities.map((activity, index) => <Paper
                                    key={activity.Title}
                                    variant="outlined"
                                    component="article"
                                    aria-label={`Activity ${index + 1}: ${activity.Title}`}
                                    sx={{ p: 1.75, height: '100%' }}>
                                    <Stack spacing={0.75}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CheckCircleRoundedIcon color="success" fontSize="small" />
                                            <Typography fontWeight={700}>{activity.Title}</Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">{activity.Description}</Typography>
                                    </Stack>
                                </Paper>)}
                            </Box>
                        </Stack>
                    </Paper>

                    <Stack spacing={2}>
                        <Paper variant="outlined" component="section" aria-labelledby="role-assignment-title" sx={{ p: 2.5 }}>
                            <Stack spacing={2} divider={<Divider flexItem />}>
                                <Typography id="role-assignment-title" variant="h6">Talent assignment</Typography>
                                <DetailRow icon={<PersonRoundedIcon />} label="Teammate" value={replay.Talent.Name} secondary={replay.Talent.Email} />
                                <DetailRow icon={<WorkOutlineRoundedIcon />} label="Position" value={replay.Talent.PositionTitle} />
                                <DetailRow icon={<AssignmentIndRoundedIcon />} label="Assessor" value={replay.Talent.AssessorName} secondary={replay.Talent.AssessorEmail} />
                            </Stack>
                        </Paper>
                        <Paper variant="outlined" component="section" aria-labelledby="active-template-title" sx={{ p: 2.5 }}>
                            <Stack spacing={1.25}>
                                <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                                    <Typography id="active-template-title" variant="h6">Active Success Profile</Typography>
                                    <Chip label={replay.TemplateStatus.toUpperCase()} color="success" size="small" />
                                </Stack>
                                <Typography fontWeight={700}>{replay.Template.TemplateName}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {replay.Template.ProficiencyGroups.length} proficiency groups · {replay.Template.Criteria.length} observable criteria
                                </Typography>
                            </Stack>
                        </Paper>
                    </Stack>
                </Box>
            </Stack>
        </DialogContent>
        <StickyDialogActions viewportOverride={viewport.mode}>
            <ButtonEx onClick={onClose} variant="text" color="inherit">Close</ButtonEx>
            <ButtonEx
                variant="contained"
                onClick={() => setView('template')}
                aria-label="Open active Field Service Technician Success Profile">
                Open active Success Profile
            </ButtonEx>
        </StickyDialogActions>
    </ResponsiveDialog>;
};

export default RoleTemplateReplayPresentation;
